import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const RouterContext = createContext(null);
const ParamsContext = createContext({});

const currentLocation = () => ({
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
  state: window.history.state,
});

export const safeDestination = (destination) => {
  if (typeof destination !== 'string') throw new Error('Navigation destination must be a string');
  if (!destination.startsWith('/') || destination.startsWith('//') || destination.includes('\\')) {
    throw new Error('Only same-origin absolute paths may be used for navigation');
  }
  return destination;
};

export const matchRoute = (pattern, pathname) => {
  const patternParts = String(pattern).split('/').filter(Boolean);
  const pathParts = String(pathname).split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const expected = patternParts[index];
    const actual = pathParts[index];
    if (expected.startsWith(':')) {
      try {
        params[expected.slice(1)] = decodeURIComponent(actual);
      } catch {
        return null;
      }
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
};

export function BrowserRouter({ children }) {
  const [location, setLocation] = useState(currentLocation);

  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  const navigate = useCallback((destination, options = {}) => {
    if (typeof destination === 'number') {
      window.history.go(destination);
      return;
    }
    const next = safeDestination(destination);
    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method](options.state ?? null, '', next);
    setLocation(currentLocation());
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export const Router = BrowserRouter;

export function Routes({ children }) {
  const { location } = useContext(RouterContext);
  const routes = React.Children.toArray(children);
  for (const route of routes) {
    if (!React.isValidElement(route)) continue;
    const params = matchRoute(route.props.path, location.pathname);
    if (params) {
      return (
        <ParamsContext.Provider value={params}>
          {route.props.element || null}
        </ParamsContext.Provider>
      );
    }
  }
  return null;
}

export function Route() {
  return null;
}

export function Navigate({ to, replace = false, state = null }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace, state });
  }, [navigate, replace, state, to]);
  return null;
}

export function useNavigate() {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useNavigate must be used inside BrowserRouter');
  return context.navigate;
}

export function useLocation() {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useLocation must be used inside BrowserRouter');
  return context.location;
}

export function useParams() {
  return useContext(ParamsContext);
}

export function useSearchParams() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParams = useCallback((next, options = {}) => {
    const value = typeof next === 'function' ? next(new URLSearchParams(location.search)) : next;
    const query = value instanceof URLSearchParams ? value.toString() : new URLSearchParams(value).toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, options);
  }, [location.hash, location.pathname, location.search, navigate]);
  return [params, setParams];
}
